import { FC, useRef } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import { Button, Card, Cluster, EmptyState, Feed, Spinner, Stack, Typography } from '@forto-dev/phoenix-ui-core'

import { createErrorFallback } from '@/fragments/create-error-fallback'
import { useLoadMoreOnScroll } from '@/utils/hooks/useLoadMoreOnScroll'
import { useLocalisation } from '@/utils/localisation'

import { ActivityFeedFilters } from './ActivityFeedFilters'
import { ActivityFeedForm } from './ActivityFeedForm'
import { ActivityFeedItem } from './ActivityFeedItem'
import { getFeedUnreadCountQueryOptions } from '../api/feed.service'
import { useEntityActivityFeed } from '../api/useEntityFeed'
import { useMarkFeedAsRead } from '../hooks/useMarkFeedAsRead'
import { useScrollToItem } from '../hooks/useScrollToItem'

export type ActivityFeedProps = {
  /** The entity ID (shipment or booking) */
  id: string
  /** Is this a v2 Shipment? */
  v2Flow: boolean
  /**
   * Additional entity IDs to fetch activities for.
   * Separate from the main entity ID so that it's clear where the activity feed is displayed.
   */
  additionalIDs?: string[]
}

const MAX_SCROLL_INDEX = 99
const pageSizeIncrement = 20

const ErrorFallback = createErrorFallback('An error occurred while loading activities')

export const ActivityFeed: FC<ActivityFeedProps> = ({ id, v2Flow, additionalIDs = [] }) => {
  const { translate } = useLocalisation()
  const entityIDs = [id, ...additionalIDs]

  const { data: unreadCounts } = useSuspenseQuery(getFeedUnreadCountQueryOptions(entityIDs))

  const totalUnreadCount = Object.values(unreadCounts).reduce((sum, count) => sum + count.unreadCount, 0)

  const scrollIndex =
    totalUnreadCount > MAX_SCROLL_INDEX ? MAX_SCROLL_INDEX : totalUnreadCount < 2 ? 0 : totalUnreadCount - 1
  const pageSize = (Math.floor(scrollIndex / pageSizeIncrement) + 1) * pageSizeIncrement

  const pageSizeRef = useRef(pageSize)
  const { fetchNextPage, hasNextPage, isFetchingNextPage, data, error } = useEntityActivityFeed({
    entityID: entityIDs,
    pageSize: pageSizeRef.current,
  })

  const items = data.pages.flatMap((page) => page.results) || []

  const feedRef = useRef<HTMLDivElement | null>(null)

  const scrollTarget = scrollIndex > 1 && items[scrollIndex] ? `feed-activity-${items[scrollIndex]?.id}` : undefined
  useScrollToItem(feedRef, scrollTarget)
  useMarkFeedAsRead(feedRef, entityIDs)

  const observedButtonRef = useRef<HTMLButtonElement | null>(null)
  useLoadMoreOnScroll(observedButtonRef, fetchNextPage, hasNextPage, isFetchingNextPage)

  return (
    <Stack>
      <Card>
        <Stack gap={16}>
          <ActivityFeedForm id={id} v2Flow={v2Flow} />
          <ActivityFeedFilters counts={data?.pages[0]?.activityCountByType} />
        </Stack>
      </Card>

      <Stack gap={24}>
        {items.length > 0 ? (
          <div ref={feedRef}>
            <Feed>
              {items.map((item) => {
                const { entityID } = item.feedInfo
                const isNew =
                  entityID && unreadCounts?.[entityID]?.activityID ? unreadCounts[entityID].activityID < item.id : false
                return (
                  <ActivityFeedItem
                    key={item.id}
                    item={item}
                    status={isNew ? translate('feed.list.status.unread') : undefined}
                    v2Flow={v2Flow}
                  />
                )
              })}
            </Feed>
          </div>
        ) : null}

        {hasNextPage ? (
          <Stack x="center" gap={24}>
            <Button ref={observedButtonRef} variant="ghost">
              {isFetchingNextPage ? (
                <Cluster gap={8} y="center">
                  <Spinner size="xs" />
                  <Typography variant="small">{translate('feed.pagination.loading')}</Typography>
                </Cluster>
              ) : (
                translate('feed.pagination.more')
              )}
            </Button>
          </Stack>
        ) : null}

        {items.length === 0 && !isFetchingNextPage && !error && (
          <EmptyState title={translate('feed.empty.title')} description={translate('feed.empty.description')} />
        )}

        {error && (
          <ErrorFallback
            error={error}
            reset={() => {
              /* do nothing, since this is for the route error boundary */
            }}
          />
        )}
      </Stack>
    </Stack>
  )
}
